import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Send } from "lucide-react";

const convo = [
  { who: "agent", text: "Hi! I'm the OwnPBX assistant. How can I help?" },
  { who: "user", text: "Do you support calls in Hindi?" },
  { who: "agent", text: "Bilkul! English, Hindi, Hinglish aur 10+ Indian languages — sab covered hai." },
  { who: "user", text: "Great. Can I get a demo?" },
  { who: "agent", text: "Of course — booking you a live demo now. What time works?" },
];

const TypingDots = () => (
  <span className="inline-flex gap-1 items-center px-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-neutral-400"
        animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </span>
);

export const WebChatWidget = () => {
  const [count, setCount] = useState(1);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let mounted = true;
    const tick = () => {
      setCount((c) => {
        const next = c >= convo.length ? 1 : c + 1;
        return next;
      });
    };
    const id = setInterval(() => {
      if (!mounted) return;
      setTyping(true);
      setTimeout(() => {
        if (!mounted) return;
        setTyping(false);
        tick();
      }, 700);
    }, 2200);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const shown = convo.slice(0, count);

  return (
    <div className="relative bg-[#0a0a0a] border border-black shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]" data-testid="web-chat-widget">
      {/* faux browser bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="ml-3 flex-1 text-center font-mono-t text-[11px] text-neutral-500 bg-white/5 py-1">yourbusiness.com</span>
      </div>

      {/* faux page content */}
      <div className="relative p-6 min-h-[360px]">
        <div className="space-y-2 opacity-30">
          <div className="h-5 w-1/2 bg-white/20" />
          <div className="h-2.5 w-4/5 bg-white/10" />
          <div className="h-2.5 w-3/5 bg-white/10" />
          <div className="mt-4 h-16 w-full bg-white/5 border border-white/10" />
        </div>

        {/* chat widget */}
        <div className="absolute bottom-5 right-5 w-[270px] bg-white text-neutral-900 border border-black/10 shadow-2xl">
          <div className="flex items-center justify-between px-3.5 py-3 border-b border-black/10">
            <div className="flex items-center gap-2">
              <span className="relative grid place-items-center w-7 h-7 bg-neutral-900 text-white text-[11px] font-bold font-heading">AI</span>
              <div className="leading-tight">
                <p className="text-[12px] font-semibold">OwnPBX Assistant</p>
                <p className="text-[10px] text-neutral-500 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-900 animate-pulse" /> Online
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-neutral-400">
              <Minus size={14} /> <X size={14} />
            </div>
          </div>

          <div className="px-3 py-3 space-y-2 max-h-[190px] overflow-hidden flex flex-col justify-end min-h-[190px]">
            <AnimatePresence initial={false}>
              {shown.map((m, i) => (
                <motion.div
                  key={`${count}-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`text-[12px] leading-snug px-3 py-2 max-w-[85%] ${
                    m.who === "agent"
                      ? "bg-neutral-100 text-neutral-800 self-start"
                      : "bg-neutral-900 text-white self-end"
                  }`}
                >
                  {m.text}
                </motion.div>
              ))}
            </AnimatePresence>
            {typing && (
              <div className="bg-neutral-100 self-start px-2 py-1.5">
                <TypingDots />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-black/10">
            <span className="flex-1 text-[11px] text-neutral-400">Type your message…</span>
            <span className="grid place-items-center w-7 h-7 bg-neutral-900 text-white">
              <Send size={13} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
